// src/hooks/feature/useFeatureUnlock.js
// ─────────────────────────────────────────────────────────────────────────────
//  Level 0-5 Progression System
// ─────────────────────────────────────────────────────────────────────────────
//
//  Users progress through 6 levels (0-5) based on completing challenges and
//  achieving milestones. Each level unlocks new features and challenges.
//
//  Level Structure:
//    Level 0: Seeker — Just starting out
//    Level 1: Listener — "My sheep hear My voice" (John 10:27a)
//    Level 2: Follower — "They follow Me" (John 10:27c)
//    Level 3: Gathered — "I know them" (John 10:27b)
//    Level 4: Bearer — "I give them eternal life" (John 10:28)
//    Level 5: Sheepheard — "One flock, one shepherd" (John 10:16)
//
//  Progression (Levels 0-4) is computed from CHALLENGE COMPLETION — each level
//  has a fixed set of challenge ids in LEVEL_TASK_IDS, and `level` advances once
//  every challenge for the current level has been completed. Level 5 unlocks a
//  rotating challenge system instead of a fixed task list.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSyncStore } from '@/hooks/sync/useSyncStore';
import {
  STORE_FEATURE_UNLOCK_LEVEL,
  STORE_CHALLENGES,
  STORE_COMPLETED_CHALLENGES,
  STORE_PRAYER_CHECKINS,
  STORE_SERMON_NOTES,
  STORE_DAILY_ENTRIES,
  STORE_DISCIPLESHIP_DATA,
  STORE_DISCIPLESHIP_ATTENDANCE,
  STORE_EXPENSES,
  STORE_OUTGOING_SUPPORT,
  STORE_CALENDAR_EVENTS,
} from '@/lib/storeKeys';
import { CHALLENGE_LIBRARY } from '@/data/challengeLibrary';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';

// ── Level Definitions ────────────────────────────────────────────────────────
//
// FEATURE AVAILABILITY per level:
//   Level 0 (Seeker):     Core + /bible/reading-plan
//   Level 1 (Listener):   + /calendar, /people, /discipleship, /stewardship/expenses, /stewardship/people
//   Level 2 (Follower):   + /evangelism
//   Level 3 (Gathered):   + /stewardship
//   Level 4 (Bearer):     + /stewardship/reports
//   Level 5 (Sheepheard): + /discipleship/bible-study-guide + ALL remaining
//
// TASK DESIGN:
//   - Each level has 3-5 unique challenges from CHALLENGE_LIBRARY
//   - Progress = completing challenges for the current level
//   - Level 5 shows 5 rotating challenges from the library
//   - Mastery code system remains intact
//
export const UNLOCK_LEVELS = {
  0: {
    label: 'Seeker',
    emoji: '🌱',
    color: '#78716c',
    scripture: 'Matthew 11:28',
    scriptureText: '"Come to me, all you who are weary"',
    desc: 'Just starting the journey of faith',
    features: ['/', '/journal', '/prayer', '/bible', '/bible/reading-plan', '/growth', '/sermon-notes', '/settings'],
    taskCount: 3,
  },
  1: {
    label: 'Listener',
    emoji: '🐑',
    color: '#16a34a',
    scripture: 'John 10:27a',
    scriptureText: '"My sheep hear My voice"',
    desc: 'Hearing the Shepherd\'s call through His Word',
    features: ['/', '/journal', '/prayer', '/bible', '/bible/reading-plan', '/growth', '/sermon-notes', '/calendar', '/people', '/discipleship', '/stewardship/expenses', '/stewardship/people', '/settings'],
    taskCount: 4,
  },
  2: {
    label: 'Follower',
    emoji: '👣',
    color: '#0891b2',
    scripture: 'John 10:27c',
    scriptureText: '"They follow Me"',
    desc: 'Moving in consistent obedience',
    features: ['/', '/journal', '/prayer', '/bible', '/bible/reading-plan', '/growth', '/sermon-notes', '/calendar', '/people', '/discipleship', '/evangelism', '/stewardship/expenses', '/stewardship/people', '/settings'],
    taskCount: 4,
  },
  3: {
    label: 'Gathered',
    emoji: '🤝',
    color: '#d97706',
    scripture: 'John 10:27b',
    scriptureText: '"I know them"',
    desc: 'Engaging others for the gospel',
    features: ['/', '/journal', '/prayer', '/bible', '/bible/reading-plan', '/growth', '/sermon-notes', '/calendar', '/people', '/discipleship', '/evangelism', '/stewardship', '/stewardship/expenses', '/stewardship/people', '/settings'],
    taskCount: 5,
  },
  4: {
    label: 'Bearer',
    emoji: '🌿',
    color: '#7c3aed',
    scripture: 'John 10:28',
    scriptureText: '"I give them eternal life"',
    desc: 'Bearing fruit through discipleship',
    features: ['/', '/journal', '/prayer', '/bible', '/bible/reading-plan', '/growth', '/sermon-notes', '/calendar', '/people', '/discipleship', '/evangelism', '/stewardship', '/stewardship/expenses', '/stewardship/reports', '/stewardship/people', '/settings'],
    taskCount: 5,
  },
  5: {
    label: 'Sheepheard',
    emoji: '👑',
    color: '#c9a84c',
    scripture: 'John 10:16',
    scriptureText: '"One flock, one shepherd"',
    desc: 'Raising up the next generation of leaders',
    features: '*',
    taskCount: 5,
  },
};

export const ALL_FEATURES = [
  '/', '/journal', '/prayer', '/bible', '/sermon-notes',
  '/bible/reading-plan',
  '/calendar', '/growth',
  '/people', '/evangelism',
  '/discipleship', '/discipleship/bible-study-guide',
  '/stewardship', '/stewardship/expenses', '/stewardship/reports', '/stewardship/people',
  '/settings',
];

// ── Helper Functions ─────────────────────────────────────────────────────────

export function getUnlockedRoutes(level = 5) {
  const levelDef = UNLOCK_LEVELS[level];
  if (!levelDef) return ALL_FEATURES;
  if (levelDef.features === '*') return ALL_FEATURES;
  if (Array.isArray(levelDef.features)) return levelDef.features;
  return ALL_FEATURES;
}

export function isRouteUnlocked(route, level = 5) {
  const unlocked = getUnlockedRoutes(level);
  return unlocked.includes(route);
}

// ═══════════════════════════════════════════════════════════════════════════
//  CHALLENGE-BASED PROGRESSION (NEW)
// ═══════════════════════════════════════════════════════════════════════════

const LEVEL_TASK_IDS = {
  0: ['pray_3_daily', 'journal_habit', 'reading_plan_starter'],
  1: ['fast_pray', 'daily_devotional', 'sermon_notes_starter', 'calendar_setup'],
  2: ['bible_chapter', 'memory_verse', 'disc_attendance_starter', 'prayer_walk'],
  3: ['invite_someone', 'share_gospel', 'add_first_disciple', 'support_missionary', 'prayer_streak'],
  4: ['tithe_commitment', 'disciple_to_salvation', 'disciple_leads_out', 'budget_tracking', 'disciple_prayer'],
  5: null, // Level 5 uses rotating challenges (handled separately)
};

/**
 * Get challenge IDs for a specific level
 */
export function getLevelChallengeIds(level) {
  return LEVEL_TASK_IDS[level] || [];
}

/**
 * Get challenges for a specific level from the library
 * Returns tasks with their completion status
 */
export function getChallengesForLevel(level, completedChallenges = [], activeChallenges = []) {
  const taskIds = LEVEL_TASK_IDS[level];
  if (!taskIds || !Array.isArray(taskIds)) return []; // Level 5 handled separately

  // Guard: Ensure CHALLENGE_LIBRARY is a valid array
  if (!CHALLENGE_LIBRARY || typeof CHALLENGE_LIBRARY.find !== 'function') return [];

  // Guard: Ensure completedChallenges is a valid array
  if (!Array.isArray(completedChallenges)) return taskIds.map((id, index) => ({ id: `task_${index}`, key: id, label: id, done: false, route: '/growth', days: 0, category: 'unknown' }));

  const completedTemplateIds = new Set(
    completedChallenges.map(c => c.templateId).filter(Boolean)
  );

  return taskIds.map((id, index) => {
    const template = CHALLENGE_LIBRARY.find(c => c.id === id);
    if (!template) {
      return {
        id: `task_${index}`,
        key: id,
        label: id,
        done: false,
        route: '/growth',
        days: 0,
        category: 'unknown',
      };
    }

    return {
      id: `task_${index}`,
      key: template.id,
      label: `${template.emoji} ${template.label}`,
      desc: template.desc,
      done: completedTemplateIds.has(template.id),
      active: Array.isArray(activeChallenges) && activeChallenges.some(a => a.templateId === template.id),
      route: '/growth',
      challengeTemplate: template,
      days: template.days,
      category: template.category,
    };
  });
}

/**
 * Get progress for a specific level based on challenge completion
 */
export function getLevelProgressByChallenges(level, completedChallenges = []) {
  if (level >= 5) return { pct: 100, hint: 'Fully unlocked!', met: [], unmet: [] };

  const tasks = getChallengesForLevel(level, completedChallenges);
  if (tasks.length === 0) return { pct: 0, hint: '', met: [], unmet: [] };

  const met = tasks.filter(t => t.done);
  const unmet = tasks.filter(t => !t.done);
  const pct = Math.round((met.length / tasks.length) * 100);

  return {
    pct,
    hint: `${met.length}/${tasks.length} challenges completed`,
    met,
    unmet,
    total: tasks.length,
    completed: met.length,
  };
}

export function getLevelTaskProgress(level, earnedTypes = new Set()) {
  return getLevelProgressByChallenges(level, earnedTypes);
}

// (legacy getLevelProgress removed — use getLevelProgressByChallenges)

/**
 * Get level tasks for display (Levels 0-4 show tasks to leave that level).
 * Each level's own .requirements define the milestones needed to reach the next level.
 */
export function getLevelTasks(level, earnedTypes = []) {
  if (level >= 5) return [];

  // earnedTypes can be an array or Set - normalize to array
  const safeEarned = Array.isArray(earnedTypes) ? earnedTypes : [];
  // Use challenge-based tasks instead of milestone requirements
  return getChallengesForLevel(level, safeEarned);
}

/**
 * Get Level 5 cycling challenge tasks.
 * Shows the next 5 uncompleted challenges from the library.
 * As challenges are completed, they drop off and the next ones appear.
 * Custom completed challenges also count toward progress.
 */
export function getLevel5Tasks(completedChallenges = [], activeChallenges = [], roleId = 'planter') {
  // Guard against undefined CHALLENGE_LIBRARY (can happen on iOS phone mode load)
  if (!CHALLENGE_LIBRARY || typeof CHALLENGE_LIBRARY.map !== 'function') {
    return [];
  }
  const safeCompleted = Array.isArray(completedChallenges) ? completedChallenges : [];
  const safeActive = Array.isArray(activeChallenges) ? activeChallenges : [];
  const completedTemplateIds = new Set(
    safeCompleted.map(c => c.templateId).filter(Boolean)
  );
  const activeTemplateIds = new Set(
    safeActive.map(c => c.templateId).filter(Boolean)
  );

  // All library challenges not yet completed
  // Same role rule the Browse library uses: no roles = everyone
  const remaining = CHALLENGE_LIBRARY.filter(c =>
    !completedTemplateIds.has(c.id) && (!c.roles || c.roles.includes(roleId))
  );

  // Take the first 5
  const batch = remaining.slice(0, 5);

  return batch.map((c, i) => ({
    id: `l5_task_${i}`,
    key: c.id,
    label: `${c.emoji} ${c.label}`,
    desc: c.desc,
    done: false,
    active: activeTemplateIds.has(c.id),
    route: '/growth',
    challengeTemplate: c,
    days: c.days,
    category: c.category,
  }));
}

// (_unusedGetTaskRoute removed — milestone-era, unreferenced)

// (getChallengesForTask removed — unreferenced; template.contributes is now display-only metadata)

// (countCheckInDays and getLevelTaskHint removed — unreferenced; milestone-era)

export async function verifyMasterCode(code) {
  return code === 'sheepheard7';
}

// ── Main Hook ────────────────────────────────────────────────────────────────
export function useFeatureUnlock() {
  const { user } = useAuth();
  const { role: profileRole } = useProfile();
  const [storedLevel, setStoredLevel, storedLevelReady] = useSyncStore(STORE_FEATURE_UNLOCK_LEVEL, 0);

  // ── Data stores for challenge completion tracking ───────────────────────
  const [completedChallenges, , completedChallengesReady] = useSyncStore(STORE_COMPLETED_CHALLENGES, []);
  const [activeChallenges] = useSyncStore(STORE_CHALLENGES, []);

  // FIX (RoleRoute race): effectiveLevel depends on both stores below having
  // finished their async IDB/Firestore load. Exposed as `ready` so consumers
  // (RoleRoute in App.jsx) can hold off trusting isUnlocked() until then,
  // instead of evaluating it against default/unloaded values on first render.
  const ready = storedLevelReady && completedChallengesReady;

  // ── Calculate current level based on CHALLENGE COMPLETION ──────────────
  const level = useMemo(() => {
    // For each level, check if ALL challenges for that level are completed
    // Level 0 requires completing Lv.0 challenges, etc.
    for (let checkLevel = 0; checkLevel <= 4; checkLevel++) {
      const taskIds = LEVEL_TASK_IDS[checkLevel];
      if (!taskIds || taskIds.length === 0) continue;

const safeCompleted2 = Array.isArray(completedChallenges) ? completedChallenges : [];
      const completedTemplateIds = new Set(
        safeCompleted2.map(c => c.templateId).filter(Boolean)
      );

      // All challenges for this level must be completed to advance
      const allCompleted = taskIds.every(id => completedTemplateIds.has(id));
      if (!allCompleted) return checkLevel;
    }
    return 5; // All levels completed
  }, [completedChallenges]);

  useEffect(() => {
    if (!ready) return;
    if (level > storedLevel) setStoredLevel(level);
  }, [ready, level, storedLevel, setStoredLevel]);

  const levelDef = UNLOCK_LEVELS[level] || UNLOCK_LEVELS[0];
  const nextLevelDef = level < 5 ? UNLOCK_LEVELS[level + 1] : null;

  // ── Progress based on challenge completion ─────────────────────────────
  const progress = useMemo(() => {
    return getLevelProgressByChallenges(level, completedChallenges);
  }, [level, completedChallenges]);

  // effectiveLevel = max(earned, master-unlocked via mastery code)
  // isUnlocked / unlockedRoutes must use effectiveLevel so master-unlocked
  // routes are immediately accessible without requiring challenges to be completed.
  // accessLevel: mastery code may raise this. It ONLY decides which routes open.
  const accessLevel = Math.max(level, storedLevel ?? 0);
  // effectiveLevel: organic level from completed challenges. Drives the level
  // display, task list, progress bar and library locks. Mastery never changes it.
  const effectiveLevel = level;

  // ── Level tasks: 0-4 → challenge-based tasks; 5 → cycling challenges ───
  // Gate on effectiveLevel (not organic level) so mastery-unlocked users
  // see challenges immediately.
  const levelTasks = useMemo(() => {
    if (effectiveLevel >= 5) return getLevel5Tasks(completedChallenges, activeChallenges, profileRole ?? 'planter');
    return getChallengesForLevel(effectiveLevel, completedChallenges, activeChallenges);
  }, [effectiveLevel, completedChallenges, activeChallenges, profileRole]);
  const effectiveLevelDef = UNLOCK_LEVELS[effectiveLevel] || UNLOCK_LEVELS[0];
  const unlockedRoutes = getUnlockedRoutes(accessLevel);

  const isUnlocked = useCallback((route) => {
    return isRouteUnlocked(route, accessLevel);
  }, [accessLevel]);

  // unlockRoute(targetLevel: number) — called by MasteryCodeInput after a
  // valid mastery code is entered. Advances storedLevel to targetLevel so
  // effectiveLevel (and therefore isUnlocked) reflects the new access tier.
  const unlockRoute = useCallback((targetLvl) => {
    const lvl = typeof targetLvl === 'number' ? Math.min(Math.max(targetLvl, 0), 5) : 5;
    setStoredLevel(prev => Math.max(prev ?? 0, lvl));
  }, [setStoredLevel]);

  return {
    level,
    levelDef,
    effectiveLevel,
    effectiveLevelDef,
    unlockedRoutes,
    isUnlocked,
    isRouteMasterUnlocked: (route) => isRouteUnlocked(route, accessLevel) && !isRouteUnlocked(route, level),
    progress,
    nextLevel: nextLevelDef,
    levelTasks,
    levelTaskProgress: { done: progress.met.length, total: progress.met.length + progress.unmet.length },
    ready,
    UNLOCK_LEVELS,
    unlockRoute,
    isMaxLevel: effectiveLevel >= 5,
    completedChallengeCount: (completedChallenges || []).length,
    totalLibraryChallenges: Array.isArray(CHALLENGE_LIBRARY) ? CHALLENGE_LIBRARY.length : 0,
  };
}